import React, { useState } from "react";
import {
    Save,
    Quote as QuoteIcon,
    User,
    Info
} from "lucide-react";

export default function QuoteForm({ initialData = {}, onSubmit, isEditing = false }) {
    const [formData, setFormData] = useState({
        text: initialData.text || "",
        source: initialData.source || "",
        status: initialData.status || "active",
        ...initialData
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Primary Content */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white dark:bg-[#1A1A1A] p-6 lg:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "Playfair Display, serif" }}>
                            <QuoteIcon size={20} className="text-jazz-brown" /> Featured Quote
                        </h2>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Quote Text</label>
                                <textarea
                                    name="text"
                                    value={formData.text}
                                    onChange={handleChange}
                                    rows={5}
                                    placeholder="e.g., Temidayo Balogun doesn't just play jazz..."
                                    className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium italic leading-relaxed"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Source / Attribution</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-black/20" size={16} />
                                    <input
                                        type="text"
                                        name="source"
                                        value={formData.source}
                                        onChange={handleChange}
                                        placeholder="e.g., DownBeat Magazine"
                                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                    <div className="bg-white dark:bg-[#1A1A1A] p-6 lg:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <h3 className="text-lg font-bold mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                            Settings
                        </h3>
                        <div className="space-y-6">
                            <div className="flex items-center justify-between p-4 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222]">
                                <div>
                                    <p className="text-xs font-bold text-black uppercase tracking-widest">Active Status</p>
                                    <p className="text-xs text-black dark:text-white/50 uppercase tracking-widest mt-0.5">Visible on site</p>
                                </div>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className="bg-transparent text-xs font-bold uppercase tracking-widest focus:outline-none cursor-pointer"
                                >
                                    <option value="active">ACTIVE</option>
                                    <option value="disabled">DISABLED</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="bg-black text-white p-6 lg:p-8 rounded-2xl shadow-xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-jazz-brown/20 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
                        <h3 className="text-lg font-bold mb-4 relative z-10" style={{ fontFamily: "Playfair Display, serif" }}>
                            Design Note
                        </h3>
                        <div className="flex gap-3 relative z-10">
                            <Info size={16} className="text-jazz-brown flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-white leading-relaxed uppercase tracking-widest">
                                Quotes reflect authority. Choose ones that succinctly capture Temidayo's artistic essence.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <button
                            type="submit"
                            className="w-full flex items-center justify-center gap-2 bg-black text-white py-4 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all shadow-xl shadow-black/10"
                        >
                            <Save size={18} /> {isEditing ? "UPDATE QUOTE" : "PUBLISH QUOTE"}
                        </button>
                        <button
                            type="button"
                            onClick={() => window.history.back()}
                            className="w-full py-4 rounded-xl text-xs font-bold tracking-widest text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white border border-black/5 bg-white dark:bg-[#1A1A1A] transition-all"
                        >
                            CANCEL
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
}
